import PlatformLanguageSelector from "@/components/onboarding/data-form/PlatformLanguageSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { PlatformLanguage, PlatformUnits } from "@/generated-api";
import { AccountSettingsResponse } from "@/generated-api/models/AccountSettingsResponse";
import { useUpdateAccountSettings } from "@/tanstack-hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { CheckCircle2Icon } from "lucide-react";
import PlatformUnitsSelector from "../onboarding/data-form/PlatformUnitsSelector";
import { useTranslation } from "@/hooks/useTranslation";

const formSchema = z.object({
  emailNotifications: z.boolean(),
  language: z.nativeEnum(PlatformLanguage),
  units: z.nativeEnum(PlatformUnits),
});

type FormSchema = z.infer<typeof formSchema>;

export default function AccountSettingsForm({
  accountSettings,
}: {
  accountSettings: AccountSettingsResponse;
}) {
  const { toast } = useToast();
  const updateAccountSettingsMutation = useUpdateAccountSettings();
  const t = useTranslation();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      emailNotifications: accountSettings.email_notifications,
      language: accountSettings.preferred_language,
      units: accountSettings.preferred_units,
    },
  });

  function handleUpdateSettings(values: FormSchema) {
    updateAccountSettingsMutation.mutate(
      {
        email_notifications: values.emailNotifications,
        preferred_language: values.language,
        preferred_units: values.units,
      },
      {
        onSuccess: () => {
          form.reset(values);
          toast({
            variant: "default",
            // @ts-expect-error Type 'Element' is not assignable to type
            title: (
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4" />
                <span>{t.ACCOUNT_SETTINGS.LABELS.UPDATED_SUCCESS}</span>
              </div>
            ),
            className: "dark rounded-2xl",
            duration: 2000,
          });
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: t.ACCOUNT_SETTINGS.ERRORS.UPDATED_ERROR,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      }
    );
  }

  return (
    <Card className="dark w-full max-w-lg">
      <CardHeader>
        <CardTitle className="text-xl">
          {t.ACCOUNT_SETTINGS.LABELS.GENERAL_SETTINGS_SECTION_TITLE}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleUpdateSettings)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="emailNotifications"
              render={({ field }) => (
                <FormItem className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] items-center">
                  <FormLabel>
                    {t.ACCOUNT_SETTINGS.LABELS.NOTIFICATIONS_SECTION_TITLE}
                  </FormLabel>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400 mr-2">
                      {t.ACCOUNT_SETTINGS.LABELS.EMAIL_NOTIFICATIONS}
                    </span>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </div>
                </FormItem>
              )}
            />
            <div className="h-px bg-zinc-800" />
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] items-center">
                  <FormLabel>
                    {t.ACCOUNT_SETTINGS.LABELS.LANGUAGE_SETTING}
                  </FormLabel>
                  <div>
                    <FormControl>
                      <PlatformLanguageSelector formField={field} />
                    </FormControl>
                    <FormMessage className="mt-1" />
                  </div>
                </FormItem>
              )}
            />
            <div className="h-px bg-zinc-800" />
            <FormField
              control={form.control}
              name="units"
              render={({ field }) => (
                <FormItem className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] items-center">
                  <FormLabel>
                    {t.ACCOUNT_SETTINGS.LABELS.UNITS_SETTING}
                  </FormLabel>
                  <div>
                    <FormControl>
                      <PlatformUnitsSelector formField={field} />
                    </FormControl>
                    <FormMessage className="mt-1" />
                  </div>
                </FormItem>
              )}
            />
            <div className="h-px bg-zinc-800" />
            <Button
              type="submit"
              className="w-full"
              disabled={
                updateAccountSettingsMutation.isPending ||
                !form.formState.isDirty ||
                form.watch("language").length === 0
              }
            >
              {updateAccountSettingsMutation.isPending
                ? t.SHARED.LABELS.SENDING_BUTTON
                : t.SHARED.LABELS.SAVE_BUTTON}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
